/**
 * DayDrawer.js — Bottom-sheet day attendance detail
 *
 * Single source of truth for the attendance detail UI — used by both
 * ViewWorkerPage (calendar/list row tap) and HomePage ("View shift" button,
 * approve action, add-note, view summary). AttendanceDetailModal has been removed.
 *
 * Layout (single scroll, no tabs):
 *   Header   — worker avatar + name + date + status badge + close
 *   Shift    — punch-in/out cards, break table, totals strip
 *   Approval — wages form + approve toggle (manager only, status=checkout)
 *   Earnings — day earnings list + "Add bonus/allowance" inline form (manager only)
 *   Payouts  — advance payments on this day
 *   Notes    — day notes with add-note input (manager only)
 *   Footer   — sticky Close button
 *
 * Props:
 *   record     {AttendanceRecord}  — the attendance row being viewed
 *   onClose    {() => void}        — dismiss callback
 *   onApproved {() => void}        — called after successful approval
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Avatar, Modal, showAlert } from './UI';
import { playSuccessSound, playErrorSound } from '../utils/audio';
import PaymentModal from './PaymentModal';
import {
  getDaysEarningsForWorker,
  getAttendanceNotes,
  getDaysPayoutsForWorker,
  addEarnings,
  addNote,
  updateNote,
  approveAttendance,
  getShifts,
  getRates,
  updatePunchTimestamp,
  startShift,
  endShift,
  startBreak,
  endBreak,
  markAbsent,
  markAbsentWithReason,
  markLeave,
  markWeeklyOff,
  resetAttendance,
  getLeaveTypeByCode,
  getLeavePolicy,
  getApprovedLeaveRequestsForDate,
  createDeduction,
  createLeaveDeduction,
  getDaysDeductionsForWorker,
  patchAttendance,
  setAttendanceApproved,
} from '../api/directus';
import {
  formatCurrency,
  formatDate,
  formatDateTime,
  formatDuration,
  getShiftDuration,
  getHourlyRate,
} from '../utils/helpers';
import { useAuth } from '../context/AuthContext';

/**
 * Single source of truth for shift/break/net minutes on a record.
 * Prefers the stored `shift_duration`/`break_duration` aggregates, but both
 * can be null/0 (e.g. a backend flow that never populated them, or a bulk
 * write that cleared them) — in that case derive from punch timestamps
 * instead. `nowMs` is only used as a last resort, when the shift has no
 * punch_out yet and is genuinely still running; it must never stand in for
 * punch_out on a completed shift, or elapsed-since-punch-in balloons to
 * however long the record sat unreviewed.
 */
function getEffectiveDurations(record, nowMs) {
  if (!record) return { shiftMin: 0, breakMin: 0, netMin: 0 };

  const derivedBreakMin = record.breaks?.reduce((s, b) => s + (b.duration || 0), 0) ?? 0;
  const breakMin = record.break_duration > 0 ? record.break_duration : derivedBreakMin;

  const pIn  = record.punch_in?.timestamp;
  const pOut = record.punch_out?.timestamp;
  let shiftMin;
  if (record.shift_duration != null) {
    shiftMin = record.shift_duration;
  } else if (pIn && pOut) {
    shiftMin = Math.floor((new Date(pOut).getTime() - new Date(pIn).getTime()) / 60_000);
  } else if (pIn) {
    shiftMin = Math.floor((nowMs - new Date(pIn).getTime()) / 60_000);
  } else {
    shiftMin = 0;
  }

  return { shiftMin, breakMin, netMin: Math.max(0, shiftMin - breakMin) };
}

/** Compact deduction/earning hint for a leave type. */
function leaveHint(lt) {
  if (!lt) return '';
  if (lt.paid_leave) return 'Full day wages';
  const f = parseFloat(lt.penalty_factor ?? 0);
  if (f === 0)   return 'LOP only';
  if (f === 0.5) return 'LOP + ½ day deduction';
  if (f === 1)   return 'LOP + full day deduction';
  return `LOP + ${f}× daily rate`;
}

export default function DayDrawer({ record, onClose, onApproved }) {
  const { isManager, isAdmin } = useAuth();
  const navigate = useNavigate();

  // Day-scoped data loaded when record changes
  const [earnings, setEarnings] = useState([]);
  const [notes, setNotes] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [rates, setRates] = useState([]);
  const [saving, setSaving] = useState(false);

  // Note form
  const [noteContent, setNoteContent] = useState('');
  // Inline edit state — only one note editable at a time
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editContent,   setEditContent]   = useState('');

  // Wages / approval form
  const [selectedShiftId, setSelectedShiftId] = useState('');
  const [selectedRateId,  setSelectedRateId]  = useState('');
  const [otFactor,       setOtFactor]       = useState('2.00');
  const [stdRateInput,   setStdRateInput]   = useState(''); // '' = use calculated
  const [otRateInput,    setOtRateInput]    = useState(''); // '' = use calculated
  const [remarks,        setRemarks]        = useState('');
  const [payableHrs,     setPayableHrs]     = useState(0);
  const [payableMins,    setPayableMins]    = useState(0);
  const [confirmOpen,      setConfirmOpen]      = useState(false);
  const [ratesExpanded,    setRatesExpanded]    = useState(false); // collapse shift/rate details by default
  // Whether to include the power day bonus for this specific approval — true by default.
  // Manager can toggle it off when a worker hasn't met the power day conditions.
  const [powerDayEnabled, setPowerDayEnabled] = useState(true);
  const [paymentOpen, setPaymentOpen] = useState(false);

  // Absent picker — opens when "Mark absent" is tapped from the actions menu
  const [absentPicker, setAbsentPicker]                 = useState(false);
  const [pendingAbsentReason, setPendingAbsentReason]   = useState('');

  // Leave picker — opens when "Mark leave" is tapped from the actions menu
  const [leavePicker, setLeavePicker]                   = useState(false);
  const [leavePickerLoading, setLeavePickerLoading]     = useState(false);
  const [eligibleLeaveTypes, setEligibleLeaveTypes]     = useState([]);
  const [selectedLeaveTypeId, setSelectedLeaveTypeId]   = useState('');
  const [preApprovedRequest, setPreApprovedRequest]     = useState(null);

  // Leave approval section — editable type selector and toggleable line items
  const [leaveApprovalTypes, setLeaveApprovalTypes]     = useState([]);  // policy types
  const [approvalLeaveType, setApprovalLeaveType]       = useState(null); // full leave_type object
  const [leaveWagesEnabled, setLeaveWagesEnabled]       = useState(true); // toggle wages row
  const [leavePenaltyEnabled, setLeavePenaltyEnabled]   = useState(true); // toggle deduction row

  // Absent approval — deduction rules fetched from leave_type with code='absent'
  const [absentLeaveType, setAbsentLeaveType]           = useState(null);
  const [absentWagesEnabled, setAbsentWagesEnabled]     = useState(true);
  const [absentPenaltyEnabled, setAbsentPenaltyEnabled] = useState(true);

  // Day deductions
  const [deductions, setDeductions] = useState([]);

  // Add manual deduction form — mirrors add-earning form pattern
  const [addDeductionOpen,    setAddDeductionOpen]    = useState(false);
  const [addDeductionType,    setAddDeductionType]    = useState('adhoc');
  const [addDeductionAmount,  setAddDeductionAmount]  = useState('');
  const [addDeductionRemarks, setAddDeductionRemarks] = useState('');

  // Ad-hoc earning form (bonus / allowance) — manager only, any record status
  // Status-actions dropdown
  const [actionsOpen,    setActionsOpen]    = useState(false);
  const actionsMenuRef = useRef(null);

  const [addEarningOpen,    setAddEarningOpen]    = useState(false);
  const [addEarningType,    setAddEarningType]    = useState('bonus');
  const [addEarningAmount,  setAddEarningAmount]  = useState('');
  const [addEarningRemarks, setAddEarningRemarks] = useState('');

  const [now, setNow] = useState(() => Date.now());
  const [editingPunchIn,  setEditingPunchIn]  = useState(false);
  const [editingPunchOut, setEditingPunchOut] = useState(false);
  const [editingBreakId,  setEditingBreakId]  = useState(null);
  const [punchInDraft,  setPunchInDraft]  = useState('');
  const [punchOutDraft, setPunchOutDraft] = useState('');
  const [breakInDraft,  setBreakInDraft]  = useState('');
  const [breakOutDraft, setBreakOutDraft] = useState('');
  // Single active tooltip key — only one open at a time; null = all closed
  const [activeTooltip, setActiveTooltip] = useState(null);

  const worker = record?.worker || {};
  const punchIn = record?.punch_in?.timestamp;
  const punchOut = record?.punch_out?.timestamp;

  // Approval is tracked on is_approved; any terminal status can be approved.
  // 'approved' status check is a fallback for legacy records pre-migration.
  const APPROVABLE = ['checkout', 'absent', 'leave', 'weekly_off'];
  const isApproved        = record?.is_approved === true || record?.status === 'approved';
  const isPendingApproval = APPROVABLE.includes(record?.status) && !isApproved;
  // Separate flags drive the two approval form variants
  const isCheckoutPending  = record?.status === 'checkout'  && !isApproved;
  const isNonShiftPending  = ['absent', 'leave', 'weekly_off'].includes(record?.status) && !isApproved;

  // Load day-level data in parallel when record changes
  useEffect(() => {
    if (!record) return;
    const wid = worker.id;
    const date = record.date;
    setNoteContent('');
    setAbsentPicker(false);
    setPendingAbsentReason('');
    setLeavePicker(false);
    setApprovalLeaveType(null);
    setLeaveApprovalTypes([]);
    setLeaveWagesEnabled(true);
    setLeavePenaltyEnabled(true);
    setAbsentLeaveType(null);
    setAbsentWagesEnabled(true);
    setAbsentPenaltyEnabled(true);
    setAddDeductionOpen(false);
    setAddDeductionAmount('');
    setAddDeductionRemarks('');

    // Day-specific data — each promise is independent so one failure doesn't block others
    getDaysEarningsForWorker(wid, date).then(r => setEarnings(r.data || [])).catch(() => {});
    getDaysDeductionsForWorker(wid, date).then(r => setDeductions(r.data || [])).catch(() => {});
    getAttendanceNotes(record.id).then(r => setNotes(r.data || [])).catch(() => {});
    getDaysPayoutsForWorker(wid, date).then(r => setPayouts(r.data || [])).catch(() => {});

    // Pre-fill shift/rate from the attendance snapshot — these are frozen at
    // day-creation time so they reflect the worker's config on that specific day,
    // not their current (potentially changed) worker record.
    if (record.shift) {
      const sid = typeof record.shift === 'object' ? record.shift.id : record.shift;
      setSelectedShiftId(String(sid));
    }
    if (record.rate) {
      const rid = typeof record.rate === 'object' ? record.rate.id : record.rate;
      setSelectedRateId(String(rid));
    }

    // For absent records awaiting approval, load the leave_type with code='absent'
    // so the approval handler can apply the configured deduction rules.
    if (record.status === 'absent' && !record.is_approved) {
      getLeaveTypeByCode('absent').then(lt => setAbsentLeaveType(lt)).catch(() => {});
    }

    // For leave records awaiting approval, load the worker's policy types so the
    // manager can change the leave type if needed before approving.
    if (record.status === 'leave' && !record.is_approved) {
      const policyId = worker.leave_policy?.id ?? worker.leave_policy;
      if (policyId) {
        getLeavePolicy(policyId)
          .then(r => {
            const types = (r.data?.leave_types ?? []).map(lt => lt.leave_types_id).filter(Boolean);
            setLeaveApprovalTypes(types);
          })
          .catch(() => {});
      }
      // Initialise the approval type from the current attendance record
      const lt = record.leave_type;
      if (lt && typeof lt === 'object') setApprovalLeaveType(lt);
    }
  }, [record]); // worker is derived from record; changing record re-runs the effect

  // Shifts and rates are static reference data — load once on mount
  useEffect(() => {
    getShifts().then(r => setShifts(r.data || [])).catch(() => {});
    getRates().then(r => setRates(r.data || [])).catch(() => {});
  }, []);

  // Lock background scroll while the drawer is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  // Intercept the phone back button so it closes the drawer instead of navigating away
  useEffect(() => {
    history.pushState({ daydrawer: true }, '');
    function onPop() { onClose(); }
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []); // eslint-disable-line


  // Close any open tooltip when the user clicks/taps outside a tooltip trigger
  useEffect(() => {
    if (!activeTooltip) return;
    function handleOutside(e) {
      if (!e.target.closest('.p-sched-wrap')) setActiveTooltip(null);
    }
    document.addEventListener('pointerdown', handleOutside);
    return () => document.removeEventListener('pointerdown', handleOutside);
  }, [activeTooltip]);

  // Close the status-actions dropdown when tapping outside it
  useEffect(() => {
    if (!actionsOpen) return;
    function handleOutside(e) {
      if (!actionsMenuRef.current?.contains(e.target)) setActionsOpen(false);
    }
    document.addEventListener('pointerdown', handleOutside);
    return () => document.removeEventListener('pointerdown', handleOutside);
  }, [actionsOpen]);

  // Live clock — updates every minute while shift is running so totals stay current
  useEffect(() => {
    const running = ['present', 'break'].includes(record?.status);
    if (!running) return;
    const timer = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(timer);
  }, [record?.status]);

  // When the selected rate changes, default the OT factor to that rate's ot_factor
  useEffect(() => {
    const rate = rates.find(r => String(r.id) === String(selectedRateId));
    if (rate?.ot_factor != null) setOtFactor(parseFloat(rate.ot_factor).toFixed(2));
  }, [selectedRateId, rates]);

  // Reset both rate overrides when shift or rate plan changes
  useEffect(() => {
    setStdRateInput('');
    setOtRateInput('');
  }, [selectedShiftId, selectedRateId]);

  // OT factor change: recalculate OT rate but preserve any std rate override
  useEffect(() => {
    setOtRateInput('');
  }, [otFactor]);

  // Initialise payable hours from the record's productive duration when a new record opens
  useEffect(() => {
    if (!record) return;
    const { netMin } = getEffectiveDurations(record, Date.now());
    const rounded = Math.round((netMin / 60) * 4) / 4; // snap to nearest 0.25 h
    setPayableHrs(Math.floor(rounded));
    setPayableMins(Math.round((rounded - Math.floor(rounded)) * 60));
    setRemarks('');
    // Reset the ad-hoc earning form whenever we switch to a different record
    setAddEarningOpen(false);
    setAddEarningAmount('');
    setAddEarningRemarks('');
    setPowerDayEnabled(true); // reset per record so the bonus is always on by default
  }, [record?.id]);

  // ── Helpers ────────────────────────────────────────────────────────────────

  /** Convert a UTC ISO timestamp to a datetime-local string in IST (no tz suffix). */
  function toDatetimeLocal(iso) {
    if (!iso) return '';
    const istMs = new Date(iso).getTime() + 5.5 * 60 * 60 * 1000;
    const d = new Date(istMs);
    const p = n => String(n).padStart(2, '0');
    return `${d.getUTCFullYear()}-${p(d.getUTCMonth()+1)}-${p(d.getUTCDate())}T${p(d.getUTCHours())}:${p(d.getUTCMinutes())}`;
  }
  /** Append IST offset to a datetime-local value for storage. */
  const fromDatetimeLocal = v => `${v}:00+05:30`;

  /**
   * Save earnings record then approve attendance in one atomic sequence.
   * Derived values (stdRate, totalAmount etc.) are captured from the render
   * closure at call time — always reflects the latest form state.
   */
  async function handleApproveAttendance() {
    if (!selectedShiftId || !selectedRateId) return;
    setSaving(true);
    const common = {
      worker:     worker.id,
      date:       record.date,
      attendance: record.id,
      rate:       parseInt(selectedRateId),
      shift:      parseInt(selectedShiftId),
    };
    try {
      // Always create a wages record for the standard hours
      await addEarnings({
        ...common,
        type:       'wages',
        wage_rate:  parseFloat(effectiveStdRate.toFixed(4)),
        wage_hours: parseFloat(standardHrs.toFixed(4)),
        OT_rate:    0,
        OT_hours:   0,
        amount:     parseFloat(regularPayout.toFixed(2)),
        remarks,
      });
      // Create a separate overtime record only when OT hours exist
      if (otHrsVal > 0) {
        await addEarnings({
          ...common,
          type:       'overtime',
          wage_rate:  0,
          wage_hours: 0,
          OT_rate:    parseFloat(effectiveOtRate.toFixed(4)),
          OT_hours:   parseFloat(otHrsVal.toFixed(4)),
          amount:     parseFloat(otPayoutVal.toFixed(2)),
          remarks:    '',
        });
      }
      // Add flat power day bonus when the date is an approved power day
      // and the worker's rate plan has has_powerDay enabled
      if (powerDayBonus > 0) {
        await addEarnings({
          ...common,
          type:    'bonus',
          amount:  parseFloat(powerDayBonus.toFixed(2)),
          remarks: '⚡ Power Day Bonus',
        });
      }
      await approveAttendance(record.id);
      showAlert('Attendance approved', 'success');
      playSuccessSound();
      onApproved?.();
      onClose();
    } catch (e) {
      showAlert(e?.errors?.[0]?.message || 'Approval failed', 'error');
      playErrorSound();
    } finally {
      setSaving(false);
      setConfirmOpen(false);
    }
  }

  /**
   * Approve a weekly_off record when the worker's rate plan has paid_weeklyOffs=true.
   * Creates a wages earning for the full standard shift duration (no OT) before locking.
   * Uses the worker's pre-filled shift and rate — no manual adjustments for weekly off pay.
   */
  async function handleApproveWeeklyOff() {
    if (!selectedShiftId || !selectedRateId) return;
    setSaving(true);
    try {
      await addEarnings({
        worker:     worker.id,
        date:       record.date,
        attendance: record.id,
        rate:       parseInt(selectedRateId),
        shift:      parseInt(selectedShiftId),
        type:       'wages',
        wage_rate:  parseFloat(stdRate.toFixed(4)),
        wage_hours: parseFloat(shiftDurHrs.toFixed(4)),
        OT_rate:    0,
        OT_hours:   0,
        amount:     parseFloat((shiftDurHrs * stdRate).toFixed(2)),
        remarks:    'Weekly off pay',
      });
      await setAttendanceApproved(record.id);
      showAlert('Attendance approved', 'success');
      playSuccessSound();
      onApproved?.();
      onClose();
    } catch (e) {
      showAlert(e?.errors?.[0]?.message || 'Approval failed', 'error');
      playErrorSound();
    } finally {
      setSaving(false);
    }
  }

  /** Approve a non-shift record (absent / unpaid weekly_off) — no earnings needed. */
  async function handleApproveSimple() {
    setSaving(true);
    try {
      await setAttendanceApproved(record.id);
      showAlert('Record approved', 'success');
      playSuccessSound();
      onApproved?.();
      onClose();
    } catch (e) {
      showAlert(e?.errors?.[0]?.message || 'Approval failed', 'error');
      playErrorSound();
    } finally {
      setSaving(false);
    }
  }

  /**
   * Approve an absent record using the deduction rules from the leave_type
   * with code='absent'. Falls back to simple approval if that leave_type
   * hasn't been configured in Directus.
   */
  async function handleApproveAbsent() {
    const lt = absentLeaveType;
    if (!lt) return handleApproveSimple(); // graceful fallback
    setSaving(true);
    try {
      if (lt.paid_leave && absentWagesEnabled && selectedShift && selectedRate) {
        await addEarnings({
          worker:     worker.id,
          date:       record.date,
          attendance: record.id,
          type:       'wages',
          wage_rate:  parseFloat(stdRate.toFixed(4)),
          wage_hours: parseFloat(shiftDurHrs.toFixed(4)),
          OT_rate:    0,
          OT_hours:   0,
          amount:     parseFloat((shiftDurHrs * stdRate).toFixed(2)),
          rate:       parseInt(selectedRateId),
          shift:      parseInt(selectedShiftId),
          remarks:    'Absent',
          status:     'approved',
        });
      }
      if ((lt.penalty_factor ?? 0) > 0 && absentPenaltyEnabled && selectedRate) {
        await createLeaveDeduction({
          worker_id:     worker.id,
          attendance_id: record.id,
          date:          record.date,
          amount:        parseFloat((selectedRate.daily_rate * lt.penalty_factor).toFixed(2)),
          rate_id:       parseInt(selectedRateId),
          remarks:       `Absent penalty (${lt.penalty_factor}× daily rate)`,
        });
      }
      await setAttendanceApproved(record.id);
      showAlert('Absence approved', 'success');
      playSuccessSound();
      onApproved?.();
      onClose();
    } catch (e) {
      showAlert(e?.errors?.[0]?.message || 'Approval failed', 'error');
      playErrorSound();
    } finally {
      setSaving(false);
    }
  }

  /**
   * Approve a leave record.
   * Creates wages (if paid_leave) or a penalty deduction (if penalty_factor > 0)
   * before locking the record. Uses whichever leave type the manager has selected
   * in the approval section — which may differ from the original mark-leave type.
   */
  async function handleApproveLeave() {
    const lt = approvalLeaveType;
    if (!lt) return;
    setSaving(true);
    try {
      if (lt.paid_leave && leaveWagesEnabled && selectedShift && selectedRate) {
        await addEarnings({
          worker:     worker.id,
          date:       record.date,
          attendance: record.id,
          type:       'wages',
          wage_rate:  parseFloat(stdRate.toFixed(4)),
          wage_hours: parseFloat(shiftDurHrs.toFixed(4)),
          OT_rate:    0,
          OT_hours:   0,
          amount:     parseFloat((shiftDurHrs * stdRate).toFixed(2)),
          rate:       parseInt(selectedRateId),
          shift:      parseInt(selectedShiftId),
          remarks:    lt.name,
          status:     'approved',
        });
      }
      if ((lt.penalty_factor ?? 0) > 0 && leavePenaltyEnabled && selectedRate) {
        const deductionAmt = parseFloat((selectedRate.daily_rate * lt.penalty_factor).toFixed(2));
        await createLeaveDeduction({
          worker_id:     worker.id,
          attendance_id: record.id,
          date:          record.date,
          amount:        deductionAmt,
          rate_id:       parseInt(selectedRateId),
          remarks:       `${lt.name} penalty (${lt.penalty_factor}× daily rate)`,
        });
      }
      await setAttendanceApproved(record.id);
      showAlert('Leave approved', 'success');
      playSuccessSound();
      onApproved?.();
      onClose();
    } catch (e) {
      showAlert(e?.errors?.[0]?.message || 'Approval failed', 'error');
      playErrorSound();
    } finally {
      setSaving(false);
    }
  }

  function openAbsentPicker() {
    setActionsOpen(false);
    setPendingAbsentReason('');
    setAbsentPicker(true);
  }

  async function confirmMarkAbsent() {
    setSaving(true);
    try {
      const res = await markAbsentWithReason(record.id, pendingAbsentReason || null);
      showAlert(res?.message || 'Marked absent', 'success');
      playSuccessSound();
      onApproved?.();
      onClose();
    } catch (e) {
      showAlert(e?.errors?.[0]?.message || 'Failed to mark absent', 'error');
      playErrorSound();
    } finally {
      setSaving(false);
      setAbsentPicker(false);
    }
  }

  /**
   * Open the leave type picker for manual mark-leave.
   * Fetches the worker's eligible leave types (filtered by policy + approved_leave rule)
   * and any approved leave_request for today.
   */
  async function openLeavePicker() {
    setActionsOpen(false);
    setLeavePicker(true);
    setLeavePickerLoading(true);
    try {
      const policyId = worker.leave_policy?.id ?? worker.leave_policy;
      const [policyRes, approvedRes] = await Promise.all([
        policyId ? getLeavePolicy(policyId) : Promise.resolve({ data: null }),
        getApprovedLeaveRequestsForDate(worker.id, record.date),
      ]);
      const allTypes = (policyRes.data?.leave_types ?? []).map(lt => lt.leave_types_id).filter(Boolean);
      const approvedReqs = approvedRes.data ?? [];

      // Filter: non-approved_leave types always show; approved_leave types only if a
      // matching approved leave_request exists for this worker+date.
      const eligible = allTypes.filter(lt => {
        if (!lt.approved_leave) return true;
        return approvedReqs.some(req => {
          const reqTypeId = typeof req.leave_type === 'object' ? req.leave_type?.id : req.leave_type;
          return String(reqTypeId) === String(lt.id);
        });
      });

      setEligibleLeaveTypes(eligible);
      const preApproved = approvedReqs.find(req => {
        const reqTypeId = typeof req.leave_type === 'object' ? req.leave_type?.id : req.leave_type;
        return eligible.some(lt => String(lt.id) === String(reqTypeId));
      }) ?? null;
      setPreApprovedRequest(preApproved);

      if (preApproved) {
        const preTypeId = typeof preApproved.leave_type === 'object'
          ? preApproved.leave_type?.id
          : preApproved.leave_type;
        setSelectedLeaveTypeId(String(preTypeId ?? ''));
      } else {
        setSelectedLeaveTypeId(String(eligible[0]?.id ?? ''));
      }
    } catch {
      showAlert('Failed to load leave types', 'error');
      setLeavePicker(false);
    } finally {
      setLeavePickerLoading(false);
    }
  }

  /** Confirm the leave type selection and mark the attendance as leave. */
  async function confirmMarkLeave() {
    if (!selectedLeaveTypeId) return;
    setSaving(true);
    try {
      const res = await markLeave(record.id, parseInt(selectedLeaveTypeId));
      showAlert(res?.message || 'Marked on leave', 'success');
      playSuccessSound();
      onApproved?.();
      onClose();
    } catch (e) {
      showAlert(e?.errors?.[0]?.message || 'Failed to mark leave', 'error');
      playErrorSound();
    } finally {
      setSaving(false);
      setLeavePicker(false);
    }
  }

  /**
   * Called when the manager changes the leave type selector in the approval section.
   * PATCHes attendance.leave_type immediately so the record stays consistent.
   */
  async function handleApprovalLeaveTypeChange(newTypeId) {
    const lt = leaveApprovalTypes.find(t => String(t.id) === String(newTypeId));
    if (!lt) return;
    setApprovalLeaveType(lt);
    try {
      await patchAttendance(record.id, { leave_type: parseInt(newTypeId) });
    } catch {
      showAlert('Failed to update leave type', 'error');
    }
  }

  async function handleAddNote() {
    if (!noteContent.trim()) return;
    setSaving(true);
    try {
      await addNote(worker.id, record.id, noteContent.trim());
      showAlert('Note added', 'success');
      playSuccessSound();
      setNoteContent('');
      const r = await getAttendanceNotes(record.id);
      setNotes(r.data || []);
    } catch {
      showAlert('Failed to add note', 'error');
      playErrorSound();
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveNote(noteId) {
    if (!editContent.trim()) return;
    setSaving(true);
    try {
      await updateNote(noteId, editContent.trim());
      showAlert('Note updated', 'success');
      playSuccessSound();
      setEditingNoteId(null);
      const r = await getAttendanceNotes(record.id);
      setNotes(r.data || []);
    } catch {
      showAlert('Failed to update note', 'error');
      playErrorSound();
    } finally {
      setSaving(false);
    }
  }

  async function handleSavePunchIn() {
    if (!punchInDraft || !record.punch_in?.id) return;
    setSaving(true);
    try {
      await updatePunchTimestamp(record.punch_in.id, fromDatetimeLocal(punchInDraft));
      showAlert('Punch in updated', 'success');
      playSuccessSound();
      setEditingPunchIn(false);
      onClose();
    } catch (e) {
      showAlert(e?.errors?.[0]?.message || 'Failed to update punch in', 'error');
    } finally { setSaving(false); }
  }

  async function handleSavePunchOut() {
    if (!punchOutDraft || !record.punch_out?.id) return;
    setSaving(true);
    try {
      await updatePunchTimestamp(record.punch_out.id, fromDatetimeLocal(punchOutDraft));
      showAlert('Punch out updated', 'success');
      playSuccessSound();
      setEditingPunchOut(false);
      onClose();
    } catch (e) {
      showAlert(e?.errors?.[0]?.message || 'Failed to update punch out', 'error');
    } finally { setSaving(false); }
  }

  async function handleSaveBreak(b) {
    // break_in / break_out may be an expanded punch object OR a bare integer FK
    // (WebSocket O2M relations don't always expand nested objects to full depth).
    const breakInId  = typeof b.break_in  === 'object' ? b.break_in?.id  : b.break_in;
    const breakOutId = typeof b.break_out === 'object' ? b.break_out?.id : b.break_out;
    setSaving(true);
    try {
      if (breakInId  && breakInDraft)  await updatePunchTimestamp(breakInId,  fromDatetimeLocal(breakInDraft));
      if (breakOutId && breakOutDraft) await updatePunchTimestamp(breakOutId, fromDatetimeLocal(breakOutDraft));
      showAlert('Break times updated', 'success');
      playSuccessSound();
      setEditingBreakId(null);
      onApproved?.();
      onClose();
    } catch (e) {
      showAlert(e?.errors?.[0]?.message || 'Failed to update break', 'error');
    } finally { setSaving(false); }
  }


  /**
   * Create an ad-hoc bonus or allowance earning for this attendance record.
   * Always saved with status='approved' (enforced by addEarnings in directus.js).
   * Works regardless of the attendance record's current status — a manager can
   * backfill a bonus on an absent day or top up an already-approved shift.
   */
  async function handleAddEarning() {
    const amt = parseFloat(addEarningAmount);
    if (!amt || amt <= 0) return;
    setSaving(true);
    try {
      // Resolve rate FK from the attendance snapshot — not worker.rate, which may
      // have changed since this day was recorded.
      const workerRateId = record.rate
        ? (typeof record.rate === 'object' ? record.rate.id : record.rate)
        : undefined;
      await addEarnings({
        worker:     worker.id,
        date:       record.date,
        attendance: record.id,
        type:       addEarningType,
        amount:     parseFloat(amt.toFixed(2)),
        remarks:    addEarningRemarks.trim() || undefined,
        ...(workerRateId != null && { rate: workerRateId }),
      });
      showAlert('Earning added', 'success');
      playSuccessSound();
      setAddEarningOpen(false);
      setAddEarningAmount('');
      setAddEarningRemarks('');
      // Refresh the earnings list so the new entry appears immediately
      const r = await getDaysEarningsForWorker(worker.id, record.date);
      setEarnings(r.data || []);
    } catch (e) {
      showAlert(e?.errors?.[0]?.message || 'Failed to add earning', 'error');
      playErrorSound();
    } finally {
      setSaving(false);
    }
  }

  async function handleAddDeduction() {
    const amt = parseFloat(addDeductionAmount);
    if (!amt || amt <= 0) return;
    setSaving(true);
    try {
      const workerRateId = record.rate
        ? (typeof record.rate === 'object' ? record.rate.id : record.rate)
        : undefined;
      await createDeduction({
        worker_id:     worker.id,
        attendance_id: record.id,
        date:          record.date,
        amount:        parseFloat(amt.toFixed(2)),
        type:          addDeductionType,
        rate_id:       workerRateId,
        remarks:       addDeductionRemarks.trim() || undefined,
      });
      showAlert('Deduction added', 'success');
      playSuccessSound();
      setAddDeductionOpen(false);
      setAddDeductionAmount('');
      setAddDeductionRemarks('');
      getDaysDeductionsForWorker(worker.id, record.date).then(r => setDeductions(r.data || [])).catch(() => {});
    } catch (e) {
      showAlert(e?.errors?.[0]?.message || 'Failed to add deduction', 'error');
      playErrorSound();
    } finally {
      setSaving(false);
    }
  }

  const { shiftMin: effectiveShiftMin, breakMin, netMin } = getEffectiveDurations(record, now);

  // Who performed each punch (user_updated preferred over user_created)
  const punchInBy  = record?.punch_in?.user_updated?.first_name  || record?.punch_in?.user_created?.first_name  || null;
  const punchOutBy = record?.punch_out?.user_updated?.first_name || record?.punch_out?.user_created?.first_name || null;

  // Scheduled start/end from the attendance's shift snapshot — not worker.shift,
  // which reflects the current assignment and may differ from when this day occurred.
  const schedIn  = record?.shift?.in_time  ? record.shift.in_time.slice(0, 5)  : null;
  const schedOut = record?.shift?.out_time ? record.shift.out_time.slice(0, 5) : null;

  // Expected values from the snapshotted shift template
  const templateDurationMin = record?.shift ? Math.round(getShiftDuration(record.shift) * 60) : null;
  const expectedBreakMin    = record?.shift ? Math.round(parseFloat(record.shift.break_hrs || 0) * 60) : null;
  const expectedNetMin      = templateDurationMin; // productive = shift duration − break

  // Colour thresholds — TODO: replace with values from Directus settings singleton
  // when that collection is available; hardcoded defaults in the meantime.
  const BREAK_WARN_OVER_MIN = 15; // yellow if break exceeds expected by up to this many mins
  const NET_WARN_UNDER_MIN  = 60; // yellow if productive is short by up to this many mins

  // Break colour — yellow if over expected by ≤15 min, red if more
  let breakColor = null;
  if (expectedBreakMin !== null && breakMin > expectedBreakMin) {
    const over = breakMin - expectedBreakMin;
    breakColor = over > BREAK_WARN_OVER_MIN ? '#b04025' : '#c79221';
  }

  // Productive colour — yellow if short by ≤1hr, red if short by >1hr
  let netColor = null;
  if (expectedNetMin && netMin > 0) {
    const shortBy = expectedNetMin - netMin;
    if      (shortBy > NET_WARN_UNDER_MIN) netColor = '#b04025';
    else if (shortBy > 0)                  netColor = '#c79221';
  }

  /** Compact duration — omits "0m" for round hours: 60 → "1h", 75 → "1h 15m" */
  function fmtExpected(min) {
    if (min == null) return null;
    const h = Math.floor(min / 60);
    const m = min % 60;
    return m === 0 ? `${h}h` : `${h}h ${m}m`;
  }

  // Execute a status-transition action from the dropdown, then close the drawer.
  // The parent (ViewWorkerPage) refreshes via onApproved; HomePage relies on WebSocket.
  async function doStatusAction(fn, msg) {
    setActionsOpen(false);
    try {
      const res = await fn(record.id);
      showAlert(res?.message || msg, 'success');
      playSuccessSound();
      onApproved?.();
      onClose();
    } catch (e) {
      showAlert(e?.errors?.[0]?.message || 'Action failed', 'error');
      playErrorSound();
    }
  }

  // Build the list of status-transition options relevant to the current record state.
  // Approved / locked records have no available transitions.
  const statusActions = (() => {
    if (isApproved) return [];
    let actions = [];
    switch (record?.status) {
      case 'present':
        actions = [
          { label: 'Start break', icon: 'pause_circle', fn: startBreak, msg: 'Break started' },
          { label: 'End shift',   icon: 'logout',       fn: endShift,   msg: 'Shift ended'   },
        ];
        break;
      case 'break':
        actions = [
          { label: 'End break', icon: 'play_circle', fn: endBreak, msg: 'Break ended' },
          { label: 'End shift', icon: 'logout',      fn: endShift, msg: 'Shift ended' },
        ];
        break;
      case 'unmarked':
        actions = [
          { label: 'Start shift', icon: 'login',        fn: startShift,        msg: 'Shift started'   },
          { label: 'Mark absent', icon: 'cancel',       absentPicker: true,    msg: 'Marked absent'   },
          { label: 'Mark leave',  icon: 'beach_access', picker: true,          msg: 'Marked on leave' },
          { label: 'Weekly off',  icon: 'event_busy',   fn: markWeeklyOff, msg: 'Marked weekly off' },
        ];
        break;
      case 'absent':
        actions = [
          { label: 'Reverse (clock in)', icon: 'undo',       fn: startShift,    msg: 'Shift started'     },
          { label: 'Mark leave',         icon: 'beach_access', picker: true,    msg: 'Marked on leave'   },
          { label: 'Weekly off',         icon: 'event_busy',   fn: markWeeklyOff, msg: 'Marked weekly off' },
        ];
        break;
      default:
        break;
    }
    // Reset is admin-only and pointless on an already-unmarked record
    if (isAdmin && record?.status !== 'unmarked') {
      actions = [
        ...actions,
        { label: 'Reset attendance', icon: 'cancel', fn: resetAttendance, msg: 'Attendance reset', danger: true },
      ];
    }
    return actions;
  })();

  if (!record) return null;

  const dateStr = formatDate(record.date, {
    weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
  });

  // Attendance status pill — always reflects the lifecycle state, never the approval state.
  // 'approved' legacy status maps to Shift Ended (it was checkout + approval in one step).
  const statusBadge = (() => {
    switch (record.status) {
      case 'present':    return { label: 'Present',     cls: 'b-running'  };
      case 'break':      return { label: 'On Break',    cls: 'b-break'    };
      case 'checkout':
      case 'approved':   return { label: 'Shift Ended', cls: 'b-pending'  };
      case 'absent':     return { label: 'Absent',      cls: 'b-absent'   };
      case 'leave':      return { label: 'On Leave',    cls: 'b-leave'    };
      case 'weekly_off': return { label: 'Weekly Off',  cls: 'b-off'      };
      default:           return { label: 'Unmarked',    cls: 'b-neutral'  };
    }
  })();

  // Approval shield — shown for statuses that can be (or have been) approved.
  const APPROVABLE_STATUSES = ['checkout', 'approved', 'absent', 'leave', 'weekly_off'];
  const showApprovalIcon = APPROVABLE_STATUSES.includes(record.status);
  // gpp_good = shield with checkmark; gpp_maybe = shield with question mark
  const approvalIcon = isApproved
    ? { icon: 'gpp_good',  cls: 'dd-approval-icon dd-approval-icon--approved',  title: 'Approved' }
    : { icon: 'gpp_maybe', cls: 'dd-approval-icon dd-approval-icon--pending',   title: 'Awaiting approval' };

  // ── Wage approval derived values (computed each render, used in JSX + handler) ──
  const selectedShift   = shifts.find(s => String(s.id) === String(selectedShiftId));
  const selectedRate    = rates.find(r => String(r.id) === String(selectedRateId));
  const payableHours    = payableHrs + payableMins / 60;
  const shiftDurHrs     = selectedShift ? getShiftDuration(selectedShift) : 0;
  const stdRate         = (selectedShift && selectedRate) ? getHourlyRate(selectedShift, selectedRate) : 0;
  const otFactorNum     = parseFloat(otFactor) || 0;
  const otRateVal       = stdRate * otFactorNum;
  // Manager can override std/OT rate; '' means use the calculated value.
  // effectiveOtRate uses effectiveStdRate (not otRateVal) so that a manual
  // std rate change propagates to the OT rate unless OT rate is also overridden.
  const effectiveStdRate = stdRateInput !== '' ? (parseFloat(stdRateInput) || 0) : stdRate;
  const effectiveOtRate  = otRateInput  !== '' ? (parseFloat(otRateInput)  || 0) : (effectiveStdRate * otFactorNum);
  const standardHrs     = Math.min(payableHours, shiftDurHrs);
  const otHrsVal        = Math.max(0, payableHours - standardHrs);
  const regularPayout   = standardHrs * effectiveStdRate;
  const otPayoutVal     = otHrsVal * effectiveOtRate;
  const totalAmount     = regularPayout + otPayoutVal;

  // Backend marks is_powerDay on individual records; this worker may be ineligible
  // even on a global power day, so we read the field directly rather than checking
  // the power_days collection.
  const isRecordPowerDay = !!record?.is_powerDay;
  // Flat bonus applied when the record is flagged as a power day AND the rate plan
  // opts in AND the manager hasn't toggled it off for this worker.
  const powerDayBonus = powerDayEnabled && isRecordPowerDay && selectedRate?.has_powerDay
    ? (parseFloat(selectedRate.powerDay_rate) || 0)
    : 0;
  // Grand total shown to manager includes the power day bonus
  const grandTotal = totalAmount + powerDayBonus;

  return (
    <>
      <div className="drawer-scrim" onClick={onClose} />
      <div
        className="drawer day-drawer"
        role="dialog"
        aria-label="Day attendance detail"
      >
        <div className="drawer-handle" />

        {/* ── Header ── */}
        <div className="dd-head">
          <Avatar worker={worker} size={48} />
          <div className="dd-id">
            <h3 className="dd-name">{worker.name}</h3>
            <p className="dd-date">
              {dateStr}
              {isRecordPowerDay && (
                <span className="p-sched-wrap" style={{ marginLeft: 5 }}>
                  <button
                    className="p-sched-btn"
                    onClick={() => setActiveTooltip(v => v === 'powerDay' ? null : 'powerDay')}
                    aria-label="Power Day"
                  >⚡</button>
                  {activeTooltip === 'powerDay' && <div className="p-sched-tip">Power Day</div>}
                </span>
              )}
            </p>
          </div>
          <div className="dd-status-row">
            <span className={`dd-badge ${statusBadge.cls}`}>{statusBadge.label}</span>
            {showApprovalIcon && (
              <span className={approvalIcon.cls} title={approvalIcon.title}>
                <span className="material-symbols-rounded">{approvalIcon.icon}</span>
              </span>
            )}
          </div>
          <button className="icon-btn" aria-label="Close" onClick={onClose}>
            <span className="material-symbols-rounded">close</span>
          </button>
        </div>

        {/* ── Quick actions ── */}
        <div className="dd-actions">
          {worker.phone && (
            <a href={`tel:${worker.phone}`} className="dd-action-btn">
              <span className="material-symbols-rounded">call</span>Call
            </a>
          )}
          <button className="dd-action-btn" onClick={() => { onClose(); navigate(`/worker/${worker.id}`); }}>
            <span className="material-symbols-rounded">person</span>Profile
          </button>
          {isManager && (
            <button className="dd-action-btn dd-action-primary" onClick={() => setPaymentOpen(true)}>
              <span className="material-symbols-rounded">currency_rupee</span>Pay
            </button>
          )}
          {/* Status-transition dropdown — only shown when there are relevant actions */}
          {statusActions.length > 0 && (
            <div ref={actionsMenuRef} style={{ position: 'relative' }}>
              <button
                className="dd-action-btn"
                aria-expanded={actionsOpen}
                onClick={() => setActionsOpen(o => !o)}
              >
                <span className="material-symbols-rounded">more_vert</span>Actions
              </button>
              {actionsOpen && (
                <div className="dash-popover dd-status-menu">
                  {statusActions.map((a, i) => (
                    <React.Fragment key={i}>
                      {/* Separator before the first danger item */}
                      {a.danger && !statusActions[i - 1]?.danger && <hr />}
                      <button
                        className={a.danger ? 'pop-danger' : ''}
                        onClick={() =>
                          a.picker       ? openLeavePicker()  :
                          a.absentPicker ? openAbsentPicker() :
                          doStatusAction(a.fn, a.msg)
                        }
                      >
                        <span className="material-symbols-rounded">{a.icon}</span>{a.label}
                      </button>
                    </React.Fragment>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Absent reason picker (opens when "Mark absent" is tapped) ── */}
        {absentPicker && (
          <div className="dd-leave-picker">
            <select
              className="dd-leave-approval-select"
              value={pendingAbsentReason}
              onChange={e => setPendingAbsentReason(e.target.value)}
              autoFocus
            >
              <option value="">No reason specified</option>
              <option value="personal">Personal</option>
              <option value="medical">Medical</option>
              <option value="family">Family Emergency</option>
              <option value="noshow">No-show</option>
              <option value="other">Other</option>
            </select>
            <div className="dd-leave-picker-actions">
              <button className="dd-picker-cancel" onClick={() => setAbsentPicker(false)}>Cancel</button>
              <button
                className="dd-picker-confirm dd-picker-confirm--absent"
                disabled={saving}
                onClick={confirmMarkAbsent}
              >
                <span className="material-symbols-rounded">cancel</span>
                Mark absent
              </button>
            </div>
          </div>
        )}

        {/* ── Leave type picker (opens when "Mark leave" is tapped) ── */}
        {leavePicker && (
          <div className="dd-leave-picker">
            {leavePickerLoading ? (
              <p className="dd-leave-loading">Loading leave types…</p>
            ) : eligibleLeaveTypes.length === 0 ? (
              <div className="dd-leave-empty">
                <span className="material-symbols-rounded">info</span>
                <p>No leave types available for this worker on this date.</p>
                <button className="wa-quiet" onClick={() => setLeavePicker(false)}>Close</button>
              </div>
            ) : (
              <>
                {preApprovedRequest && (
                  <div className="dd-leave-preapproved">
                    <span className="material-symbols-rounded">verified</span>
                    Pre-approved: {
                      typeof preApprovedRequest.leave_type === 'object'
                        ? preApprovedRequest.leave_type?.name
                        : eligibleLeaveTypes.find(lt => String(lt.id) === String(preApprovedRequest.leave_type))?.name
                    }
                  </div>
                )}
                {/* Card list — works for 1 or many options; single option is pre-selected and non-interactive */}
                <div className="dd-leave-option-list">
                  {eligibleLeaveTypes.map(lt => {
                    const isSelected = String(selectedLeaveTypeId) === String(lt.id);
                    const isSingle   = eligibleLeaveTypes.length === 1;
                    return (
                      <div
                        key={lt.id}
                        className={`dd-leave-option${isSelected ? ' dd-leave-option--selected' : ''}${isSingle ? ' dd-leave-option--only' : ''}`}
                        role={isSingle ? undefined : 'button'}
                        tabIndex={isSingle ? undefined : 0}
                        onClick={isSingle ? undefined : () => setSelectedLeaveTypeId(String(lt.id))}
                        onKeyDown={isSingle ? undefined : e => e.key === 'Enter' && setSelectedLeaveTypeId(String(lt.id))}
                      >
                        <span className="dd-lto-name">{lt.name}</span>
                        <span className="dd-lto-hint">{leaveHint(lt)}</span>
                      </div>
                    );
                  })}
                </div>
                <div className="dd-leave-picker-actions">
                  <button className="dd-picker-cancel" onClick={() => setLeavePicker(false)}>Cancel</button>
                  <button
                    className="dd-picker-confirm"
                    disabled={saving || !selectedLeaveTypeId}
                    onClick={confirmMarkLeave}
                  >
                    <span className="material-symbols-rounded">beach_access</span>
                    Mark on leave
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        <div className="dd-body">

          {/* ── Status meta — shown for non-shift statuses instead of shift details ── */}
          {['unmarked', 'absent', 'leave', 'weekly_off'].includes(record?.status) && (
            <section className="dd-section dd-status-meta">
              {record.status === 'unmarked' && (
                <div className="dd-sm-row">
                  <span className="material-symbols-rounded dd-sm-icon">pending</span>
                  <div>
                    <p className="dd-sm-title">Not yet marked</p>
                    {record.shift?.in_time && (
                      <p className="dd-sm-sub">
                        Scheduled {record.shift.in_time.slice(0, 5)} – {record.shift.out_time?.slice(0, 5)}
                      </p>
                    )}
                  </div>
                </div>
              )}
              {record.status === 'absent' && (
                <div className="dd-sm-row">
                  <span className="material-symbols-rounded dd-sm-icon dd-sm-absent">cancel</span>
                  <div>
                    <p className="dd-sm-title">Absent</p>
                    {record.absent_reason && (
                      <p className="dd-sm-sub">{{ personal: 'Personal', medical: 'Medical', family: 'Family Emergency', noshow: 'No-show', other: 'Other' }[record.absent_reason] || record.absent_reason}</p>
                    )}
                  </div>
                </div>
              )}
              {record.status === 'leave' && (
                <div className="dd-sm-row">
                  <span className="material-symbols-rounded dd-sm-icon dd-sm-leave">beach_access</span>
                  <div>
                    <p className="dd-sm-title">{record.leave_type?.name || 'On Leave'}</p>
                    {record.leave_type && <p className="dd-sm-sub">{leaveHint(record.leave_type)}</p>}
                    {record.leave_request && (
                      <span className="dd-sm-badge">Pre-approved</span>
                    )}
                  </div>
                </div>
              )}
              {record.status === 'weekly_off' && (
                <div className="dd-sm-row">
                  <span className="material-symbols-rounded dd-sm-icon dd-sm-off">event_busy</span>
                  <div>
                    <p className="dd-sm-title">Weekly Off</p>
                  </div>
                </div>
              )}
            </section>
          )}

          {/* ── Shift — only shown for statuses that have actual punch data ── */}
          {['present', 'break', 'checkout', 'approved'].includes(record?.status) && (
          <section className="dd-section">
            <div className="dd-section-head">
              <span className="material-symbols-rounded">schedule</span>
              <h4>Shift</h4>
            </div>

            <div className="punch-grid">
              {/* ── Punch In ── */}
              <div className="punch">
                <div className="p-label-row">
                  <div className="p-label">
                    <span className="material-symbols-rounded">login</span>Punch in
                    {schedIn && (
                      <div className="p-sched-wrap">
                        <button className="p-sched-btn" onClick={() => setActiveTooltip(v => v === 'schedIn' ? null : 'schedIn')}>
                          <span className="material-symbols-rounded">info</span>
                        </button>
                        {activeTooltip === 'schedIn' && (
                          <div className="p-sched-tip">Expected at {schedIn}</div>
                        )}
                      </div>
                    )}
                  </div>
                  {isManager && punchIn && !editingPunchIn && !isApproved && (
                    <button className="p-edit-btn" onClick={() => { setPunchInDraft(toDatetimeLocal(punchIn)); setEditingPunchIn(true); }}>
                      <span className="material-symbols-rounded">edit</span>
                    </button>
                  )}
                </div>
                {editingPunchIn ? (
                  <>
                    <input className="p-edit-input" type="datetime-local" value={punchInDraft} onChange={e => setPunchInDraft(e.target.value)} />
                    <div className="p-edit-actions">
                      <button className="p-edit-cancel" onClick={() => setEditingPunchIn(false)}>Cancel</button>
                      <button className="p-edit-save" disabled={saving} onClick={handleSavePunchIn}>Save</button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="p-time num">{punchIn ? formatDateTime(punchIn) : '—'}</div>
                    {punchInBy && <div className="p-by">by {punchInBy}</div>}
                  </>
                )}
              </div>

              {/* ── Punch Out ── */}
              <div className="punch">
                <div className="p-label-row">
                  <div className="p-label">
                    <span className="material-symbols-rounded">logout</span>Punch out
                    {schedOut && (
                      <div className="p-sched-wrap">
                        <button className="p-sched-btn" onClick={() => setActiveTooltip(v => v === 'schedOut' ? null : 'schedOut')}>
                          <span className="material-symbols-rounded">info</span>
                        </button>
                        {activeTooltip === 'schedOut' && (
                          <div className="p-sched-tip">Expected at {schedOut}</div>
                        )}
                      </div>
                    )}
                  </div>
                  {isManager && punchOut && !editingPunchOut && !isApproved && (
                    <button className="p-edit-btn" onClick={() => { setPunchOutDraft(toDatetimeLocal(punchOut)); setEditingPunchOut(true); }}>
                      <span className="material-symbols-rounded">edit</span>
                    </button>
                  )}
                </div>
                {editingPunchOut ? (
                  <>
                    <input className="p-edit-input" type="datetime-local" value={punchOutDraft} onChange={e => setPunchOutDraft(e.target.value)} />
                    <div className="p-edit-actions">
                      <button className="p-edit-cancel" onClick={() => setEditingPunchOut(false)}>Cancel</button>
                      <button className="p-edit-save" disabled={saving} onClick={handleSavePunchOut}>Save</button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="p-time num">
                      {punchOut ? formatDateTime(punchOut) : punchIn ? <span className="running-mini">Running</span> : '—'}
                    </div>
                    {punchOutBy && <div className="p-by">by {punchOutBy}</div>}
                  </>
                )}
              </div>
            </div>

            {record.breaks && record.breaks.length > 0 && (
              <div className="break-table">
                <div className={`bt-row bt-head${isManager && !isApproved ? ' has-edit' : ''}`}>
                  <span>Break in</span><span>Break out</span><span>Duration</span>
                  {isManager && !isApproved && <span />}
                </div>
                {record.breaks.map((b, i) => {
                  const isEditingThis = editingBreakId === b.id;
                  const canEdit = isManager && !isApproved;
                  return (
                    <div key={i} className={`bt-row${canEdit ? ' has-edit' : ''}${isEditingThis ? ' editing' : ''}`}>
                      {isEditingThis ? (
                        <>
                          <input type="datetime-local" value={breakInDraft}  onChange={e => setBreakInDraft(e.target.value)}  className="bt-edit-input" />
                          <input type="datetime-local" value={breakOutDraft} onChange={e => setBreakOutDraft(e.target.value)} className="bt-edit-input" />
                          <span />
                          <div className="bt-edit-actions">
                            <button className="bt-edit-cancel" onClick={() => setEditingBreakId(null)}>
                              <span className="material-symbols-rounded">close</span>
                            </button>
                            <button className="bt-edit-save" disabled={saving} onClick={() => handleSaveBreak(b)}>
                              <span className="material-symbols-rounded">check</span>
                            </button>
                          </div>
                        </>
                      ) : (() => {
                        const breakInBy  = b.break_in?.user_updated?.first_name  || b.break_in?.user_created?.first_name  || null;
                        const breakOutBy = b.break_out?.user_updated?.first_name || b.break_out?.user_created?.first_name || null;
                        return (
                          <>
                            <span className="num bt-cell">
                              {formatDateTime(b.break_in?.timestamp)}
                              {breakInBy && (
                                <span className="p-sched-wrap">
                                  <button className="p-sched-btn" onClick={() => setActiveTooltip(v => v === `breakIn-${b.id}` ? null : `breakIn-${b.id}`)}>
                                    <span className="material-symbols-rounded">info</span>
                                  </button>
                                  {activeTooltip === `breakIn-${b.id}` && <div className="p-sched-tip">{breakInBy}</div>}
                                </span>
                              )}
                            </span>
                            <span className="num bt-cell">
                              {formatDateTime(b.break_out?.timestamp)}
                              {breakOutBy && (
                                <span className="p-sched-wrap">
                                  <button className="p-sched-btn" onClick={() => setActiveTooltip(v => v === `breakOut-${b.id}` ? null : `breakOut-${b.id}`)}>
                                    <span className="material-symbols-rounded">info</span>
                                  </button>
                                  {activeTooltip === `breakOut-${b.id}` && <div className="p-sched-tip">{breakOutBy}</div>}
                                </span>
                              )}
                            </span>
                            <span className="num">{formatDuration(b.duration)}</span>
                            {canEdit && (
                              <button
                                className="bt-edit-btn"
                                title="Edit times"
                                onClick={() => {
                                  const breakInTs  = typeof b.break_in  === 'object' ? b.break_in?.timestamp  : null;
                                  const breakOutTs = typeof b.break_out === 'object' ? b.break_out?.timestamp : null;
                                  setBreakInDraft(toDatetimeLocal(breakInTs));
                                  setBreakOutDraft(toDatetimeLocal(breakOutTs));
                                  setEditingBreakId(b.id);
                                }}
                              >
                                <span className="material-symbols-rounded">edit</span>
                              </button>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  );
                })}
              </div>
            )}

            <div className="dd-totals">
              <div>
                <span className="t-label">Shift</span>
                <span className="t-val num">{effectiveShiftMin > 0 ? formatDuration(effectiveShiftMin) : '—'}</span>
              </div>
              <div style={breakColor ? { background: `color-mix(in srgb, ${breakColor} 6%, var(--bg-surface))` } : {}}>
                <span className="t-label t-label-tip">
                  Break
                  {fmtExpected(expectedBreakMin) && (
                    <span className="p-sched-wrap">
                      <button className="p-sched-btn" onClick={() => setActiveTooltip(v => v === 'expBreak' ? null : 'expBreak')}>
                        <span className="material-symbols-rounded">info</span>
                      </button>
                      {activeTooltip === 'expBreak' && <div className="p-sched-tip">Expected: {fmtExpected(expectedBreakMin)}</div>}
                    </span>
                  )}
                </span>
                <span className="t-val num" style={breakColor ? { color: breakColor } : {}}>{breakMin > 0 ? formatDuration(breakMin) : '0h 0m'}</span>
              </div>
              <div style={netColor ? { background: `color-mix(in srgb, ${netColor} 6%, var(--bg-surface))` } : {}}>
                <span className="t-label t-label-tip">
                  Productive
                  {fmtExpected(expectedNetMin) && (
                    <span className="p-sched-wrap">
                      <button className="p-sched-btn" onClick={() => setActiveTooltip(v => v === 'expNet' ? null : 'expNet')}>
                        <span className="material-symbols-rounded">info</span>
                      </button>
                      {activeTooltip === 'expNet' && <div className="p-sched-tip">Expected: {fmtExpected(expectedNetMin)}</div>}
                    </span>
                  )}
                </span>
                <span className="t-val num" style={netColor ? { color: netColor } : {}}>{netMin > 0 ? formatDuration(netMin) : '—'}</span>
              </div>
            </div>
          </section>
          )} {/* end shift conditional */}

          {/* ── Approval form: wages entry for checkout records (manager only) ── */}
          {isCheckoutPending && isManager && (
            <section className="dd-section dd-approval">
              <div className="dd-section-head">
                <span className="material-symbols-rounded" style={{ color: 'var(--color-primary)' }}>verified</span>
                <h4>Approve earnings</h4>
                <span className="dd-meta">Manager only</span>
              </div>

              {/* ── Collapsible: Shift / Rate / Rate fields ── */}
              <button className="ap-rates-toggle" onClick={() => setRatesExpanded(v => !v)}>
                <div className="ap-rates-summary">
                  {selectedShift && selectedRate ? (
                    <div className="ap-rates-spread">
                      <span>₹{effectiveStdRate.toFixed(0)}/hr base</span>
                      <span>{parseFloat(otFactor).toFixed(2)}× OT</span>
                      <span>= ₹{effectiveOtRate.toFixed(0)}/hr</span>
                    </div>
                  ) : (
                    <span className="ap-rates-placeholder">Tap to select shift &amp; rate</span>
                  )}
                </div>
                <span className="material-symbols-rounded ap-rates-chevron">{ratesExpanded ? 'expand_less' : 'expand_more'}</span>
              </button>

              {ratesExpanded && (
                <div className="ap-rates-body">
                  <div className="ap-grid">
                    <label className="ap-field">
                      <span className="ap-label">Shift</span>
                      <select className="ap-input" value={selectedShiftId} onChange={e => setSelectedShiftId(e.target.value)}>
                        <option value="">Select shift…</option>
                        {shifts.map(s => <option key={s.id} value={String(s.id)}>{s.name}</option>)}
                      </select>
                    </label>
                    <label className="ap-field">
                      <span className="ap-label">Rate plan</span>
                      <select className="ap-input" value={selectedRateId} onChange={e => setSelectedRateId(e.target.value)}>
                        <option value="">Select rate…</option>
                        {rates.map(r => <option key={r.id} value={String(r.id)}>{r.name}</option>)}
                      </select>
                    </label>
                  </div>
                  <div className="ap-grid ap-grid-3">
                    <div className="ap-field">
                      <span className="ap-label">Std rate (₹/hr)</span>
                      <input className="ap-input" type="number" step="0.01"
                        value={stdRateInput !== '' ? stdRateInput : stdRate.toFixed(2)}
                        onChange={e => setStdRateInput(e.target.value)}
                        onFocus={e => { if (stdRateInput === '') setStdRateInput(stdRate.toFixed(2)); e.target.select(); }}
                      />
                    </div>
                    <div className="ap-field">
                      <span className="ap-label">OT factor</span>
                      <div className="ap-stepper">
                        <button type="button" onClick={() => { const v = Math.max(0.25, Math.round((parseFloat(otFactor)-0.25)*4)/4); setOtFactor(v.toFixed(2)); }}>−</button>
                        <span>{parseFloat(otFactor).toFixed(2)}×</span>
                        <button type="button" onClick={() => { const v = Math.min(5, Math.round((parseFloat(otFactor)+0.25)*4)/4); setOtFactor(v.toFixed(2)); }}>+</button>
                      </div>
                    </div>
                    <div className="ap-field">
                      <span className="ap-label">OT rate (₹/hr)</span>
                      <input className="ap-input" type="number" step="0.01"
                        value={otRateInput !== '' ? otRateInput : effectiveOtRate.toFixed(2)}
                        onChange={e => setOtRateInput(e.target.value)}
                        onFocus={e => { if (otRateInput === '') setOtRateInput(effectiveOtRate.toFixed(2)); e.target.select(); }}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* ── Payable hours selector ── */}
              <div className="ap-field">
                <span className="ap-label">Total payable hours</span>
                <div className="ap-hours">
                  <div className="ap-hours-row">
                    <button type="button" className="ap-hours-btn" onClick={() => setPayableHrs(h => Math.max(0, h-1))}>
                      <span className="material-symbols-rounded">remove</span>
                    </button>
                    <span className="ap-hours-val num">{payableHrs}h</span>
                    <button type="button" className="ap-hours-btn" onClick={() => setPayableHrs(h => h+1)}>
                      <span className="material-symbols-rounded">add</span>
                    </button>
                    <div className="ap-mins">
                      {[0,15,30,45].map(m => (
                        <button key={m} type="button"
                          className={`ap-min-chip${payableMins === m ? ' active' : ''}`}
                          onClick={() => setPayableMins(m)}
                        >{String(m).padStart(2,'0')}</button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Payout breakdown — every item as a full-width row */}
              <div className="ap-breakdown">
                <div className="ap-breakdown-row">
                  <span className="ap-label">Regular pay</span>
                  <span className="ap-breakdown-ref">{formatDuration(Math.round(standardHrs * 60))} × ₹{effectiveStdRate.toFixed(2)}</span>
                  <span className="ap-breakdown-amt">{formatCurrency(regularPayout)}</span>
                </div>
                <div className="ap-breakdown-row">
                  <span className="ap-label">OT pay</span>
                  <span className="ap-breakdown-ref">{formatDuration(Math.round(otHrsVal * 60))} × ₹{effectiveOtRate.toFixed(2)}</span>
                  <span className="ap-breakdown-amt">{formatCurrency(otPayoutVal)}</span>
                </div>
                {/* Power day row — shown when applicable; toggle lets manager skip it for a worker */}
                {isRecordPowerDay && selectedRate?.has_powerDay && parseFloat(selectedRate.powerDay_rate) > 0 && (
                  <div className="ap-breakdown-row">
                    <span className="ap-label" style={{ opacity: powerDayEnabled ? 1 : 0.5 }}>Power Day Bonus</span>
                    <button
                      className="ap-powerday-toggle"
                      onClick={() => setPowerDayEnabled(v => !v)}
                      title={powerDayEnabled ? 'Disable power day bonus for this worker' : 'Enable power day bonus'}
                    >
                      <span className="material-symbols-rounded" style={{ color: powerDayEnabled ? '#6f8a3a' : 'var(--fg-muted)' }}>
                        {powerDayEnabled ? 'toggle_on' : 'toggle_off'}
                      </span>
                    </button>
                    <span className="ap-breakdown-amt" style={{ opacity: powerDayEnabled ? 1 : 0.35 }}>
                      {formatCurrency(parseFloat(selectedRate.powerDay_rate) || 0)}
                    </span>
                  </div>
                )}
                <div className="ap-breakdown-row ap-breakdown-total">
                  <span className="ap-label">Total wages</span>
                  <span className="ap-breakdown-ref" />
                  <span className="ap-breakdown-amt ap-breakdown-total-amt">{formatCurrency(grandTotal)}</span>
                </div>
              </div>

              {/* Remarks */}
              <div className="ap-field" style={{ margin: '10px 0 14px' }}>
                <span className="ap-label">Remarks</span>
                <input className="ap-input" placeholder="Optional note" value={remarks} onChange={e => setRemarks(e.target.value)} />
              </div>

              <button className="ap-approve-btn" disabled={saving || !totalAmount} onClick={() => setConfirmOpen(true)}>
                <span className="material-symbols-rounded">check_circle</span>
                Approve attendance
              </button>
            </section>
          )}

          {/* ── Approval: absent / leave / weekly_off (manager only) ── */}
          {isNonShiftPending && isManager && (
            <section className="dd-section dd-approval">
              <div className="dd-section-head">
                <span className="material-symbols-rounded" style={{ color: 'var(--color-primary)' }}>verified</span>
                <h4>Approve record</h4>
                <span className="dd-meta">Manager only</span>
              </div>

              {record.status === 'weekly_off' ? (
                // Weekly off has two paths depending on the rate plan
                selectedRate?.paid_weeklyOffs && shiftDurHrs > 0 && stdRate > 0 ? (
                  // Paid plan — show pay breakdown and create earnings on approval
                  <>
                    <p className="dd-readonly-text" style={{ marginBottom: 10 }}>
                      This worker's plan includes paid weekly offs. A wages record will be created on approval.
                    </p>
                    <div className="ap-breakdown" style={{ marginBottom: 14 }}>
                      <div className="ap-breakdown-row">
                        <span className="ap-label">Weekly off pay</span>
                        <span className="ap-breakdown-ref">{formatDuration(Math.round(shiftDurHrs * 60))} × ₹{stdRate.toFixed(2)}</span>
                        <span className="ap-breakdown-amt">{formatCurrency(shiftDurHrs * stdRate)}</span>
                      </div>
                      <div className="ap-breakdown-row ap-breakdown-total">
                        <span className="ap-label">Total</span>
                        <span className="ap-breakdown-ref" />
                        <span className="ap-breakdown-amt ap-breakdown-total-amt">{formatCurrency(shiftDurHrs * stdRate)}</span>
                      </div>
                    </div>
                    <button className="ap-approve-btn" disabled={saving} onClick={handleApproveWeeklyOff}>
                      <span className="material-symbols-rounded">check_circle</span>
                      Approve &amp; record pay
                    </button>
                  </>
                ) : (
                  // Unpaid plan — inform manager, approve without earnings
                  <>
                    <div className="dd-unpaid-notice">
                      <span className="material-symbols-rounded">info</span>
                      <p>This worker's rate plan does not include paid weekly offs. No earnings will be recorded.</p>
                    </div>
                    <button className="ap-approve-btn" disabled={saving} onClick={handleApproveSimple}>
                      <span className="material-symbols-rounded">check_circle</span>
                      Approve record
                    </button>
                  </>
                )
              ) : record.status === 'leave' ? (
                // ── Leave approval — show leave type, allow type change, preview deductions ──
                (() => {
                  const lt = approvalLeaveType;
                  const penalty = lt ? parseFloat(lt.penalty_factor ?? 0) : 0;
                  const penaltyAmt = selectedRate ? parseFloat((selectedRate.daily_rate * penalty).toFixed(2)) : 0;
                  const wagesAmt  = (lt?.paid_leave && selectedShift && selectedRate)
                    ? parseFloat((shiftDurHrs * stdRate).toFixed(2))
                    : 0;
                  const linkedReq = record.leave_request;
                  const approvedBy = linkedReq?.user_approved;
                  return (
                    <>
                      {/* Linked leave request info strip */}
                      {linkedReq && (
                        <div className="dd-leave-request-info">
                          <span className="material-symbols-rounded">verified</span>
                          <div>
                            <strong>Pre-approved leave</strong>
                            {approvedBy && (
                              <span> · Approved by {approvedBy.first_name} {approvedBy.last_name}
                                {linkedReq.date_approved && ` on ${formatDate(linkedReq.date_approved)}`}
                              </span>
                            )}
                            {linkedReq.reason && <p className="dd-leave-reason">{linkedReq.reason}</p>}
                          </div>
                        </div>
                      )}

                      {/* Editable leave type selector */}
                      <div className="dd-leave-type-row">
                        <label className="dd-leave-type-label">Leave type</label>
                        {leaveApprovalTypes.length <= 1 ? (
                          // Single or no options — display as readonly text
                          <span className="dd-leave-type-value">
                            {lt?.name ?? 'Not set'}
                          </span>
                        ) : (
                          <select
                            className="dd-leave-approval-select"
                            value={lt ? String(lt.id) : ''}
                            onChange={e => handleApprovalLeaveTypeChange(e.target.value)}
                          >
                            {!lt && <option value="">Select type…</option>}
                            {leaveApprovalTypes.map(t => (
                              <option key={t.id} value={String(t.id)}>
                                {t.name} — {leaveHint(t)}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>

                      {/* Tabular breakdown — matches checkout earnings style */}
                      {lt ? (
                        <div className="ap-breakdown">
                          {lt.paid_leave ? (
                            /* Wages row — toggleable like power day bonus */
                            <div className="ap-breakdown-row">
                              <span className="ap-label" style={{ opacity: leaveWagesEnabled ? 1 : 0.4 }}>Wages</span>
                              <span className="ap-breakdown-ref">{formatDuration(Math.round(shiftDurHrs * 60))} × ₹{stdRate.toFixed(2)}</span>
                              <button className="ap-powerday-toggle" onClick={() => setLeaveWagesEnabled(v => !v)}>
                                <span className="material-symbols-rounded" style={{ color: leaveWagesEnabled ? '#6f8a3a' : 'var(--fg-muted)' }}>
                                  {leaveWagesEnabled ? 'toggle_on' : 'toggle_off'}
                                </span>
                              </button>
                              <span className="ap-breakdown-amt" style={{ opacity: leaveWagesEnabled ? 1 : 0.35 }}>{formatCurrency(wagesAmt)}</span>
                            </div>
                          ) : null}
                          {penalty > 0 ? (
                            /* Deduction row — toggleable */
                            <div className="ap-breakdown-row">
                              <span className="ap-label" style={{ opacity: leavePenaltyEnabled ? 1 : 0.4, color: leavePenaltyEnabled ? '#8a2f1a' : undefined }}>Deduction</span>
                              <span className="ap-breakdown-ref">₹{selectedRate?.daily_rate} × {penalty}</span>
                              <button className="ap-powerday-toggle" onClick={() => setLeavePenaltyEnabled(v => !v)}>
                                <span className="material-symbols-rounded" style={{ color: leavePenaltyEnabled ? '#b04025' : 'var(--fg-muted)' }}>
                                  {leavePenaltyEnabled ? 'toggle_on' : 'toggle_off'}
                                </span>
                              </button>
                              <span className="ap-breakdown-amt" style={{ opacity: leavePenaltyEnabled ? 1 : 0.35, color: '#8a2f1a' }}>−{formatCurrency(penaltyAmt)}</span>
                            </div>
                          ) : null}
                          {/* Net impact total or LOP-only notice */}
                          {(lt.paid_leave || penalty > 0) ? (
                            <div className="ap-breakdown-row ap-breakdown-total">
                              <span className="ap-label">Net impact</span>
                              <span className="ap-breakdown-ref" />
                              <span className="ap-breakdown-amt ap-breakdown-total-amt">
                                {formatCurrency(
                                  (lt.paid_leave && leaveWagesEnabled ? wagesAmt : 0) -
                                  (penalty > 0 && leavePenaltyEnabled ? penaltyAmt : 0)
                                )}
                              </span>
                            </div>
                          ) : (
                            <div className="dd-lcp-lop dd-leave-calc-preview">
                              <span className="material-symbols-rounded">info</span>
                              <span>Loss of Pay only — no earnings or deductions</span>
                            </div>
                          )}
                        </div>
                      ) : null}

                      <button
                        className="ap-approve-btn"
                        disabled={saving || !lt}
                        onClick={handleApproveLeave}
                      >
                        <span className="material-symbols-rounded">check_circle</span>
                        Approve leave
                      </button>
                    </>
                  );
                })()
              ) : (
                // absent — apply deduction rules from leave_type code='absent'
                (() => {
                  const lt = absentLeaveType;
                  const penalty    = lt ? parseFloat(lt.penalty_factor ?? 0) : 0;
                  const penaltyAmt = selectedRate ? parseFloat((selectedRate.daily_rate * penalty).toFixed(2)) : 0;
                  const wagesAmt   = (lt?.paid_leave && selectedShift && selectedRate)
                    ? parseFloat((shiftDurHrs * stdRate).toFixed(2)) : 0;
                  return (
                    <>
                      {lt ? (
                        <div className="ap-breakdown">
                          {lt.paid_leave ? (
                            <div className="ap-breakdown-row">
                              <span className="ap-label" style={{ opacity: absentWagesEnabled ? 1 : 0.4 }}>Wages</span>
                              <span className="ap-breakdown-ref">{formatDuration(Math.round(shiftDurHrs * 60))} × ₹{stdRate.toFixed(2)}</span>
                              <button className="ap-powerday-toggle" onClick={() => setAbsentWagesEnabled(v => !v)}>
                                <span className="material-symbols-rounded" style={{ color: absentWagesEnabled ? '#6f8a3a' : 'var(--fg-muted)' }}>
                                  {absentWagesEnabled ? 'toggle_on' : 'toggle_off'}
                                </span>
                              </button>
                              <span className="ap-breakdown-amt" style={{ opacity: absentWagesEnabled ? 1 : 0.35 }}>{formatCurrency(wagesAmt)}</span>
                            </div>
                          ) : null}
                          {penalty > 0 ? (
                            <div className="ap-breakdown-row">
                              <span className="ap-label" style={{ opacity: absentPenaltyEnabled ? 1 : 0.4, color: absentPenaltyEnabled ? '#8a2f1a' : undefined }}>Deduction</span>
                              <span className="ap-breakdown-ref">₹{selectedRate?.daily_rate} × {penalty}</span>
                              <button className="ap-powerday-toggle" onClick={() => setAbsentPenaltyEnabled(v => !v)}>
                                <span className="material-symbols-rounded" style={{ color: absentPenaltyEnabled ? '#b04025' : 'var(--fg-muted)' }}>
                                  {absentPenaltyEnabled ? 'toggle_on' : 'toggle_off'}
                                </span>
                              </button>
                              <span className="ap-breakdown-amt" style={{ opacity: absentPenaltyEnabled ? 1 : 0.35, color: '#8a2f1a' }}>−{formatCurrency(penaltyAmt)}</span>
                            </div>
                          ) : null}
                          {(lt.paid_leave || penalty > 0) ? (
                            <div className="ap-breakdown-row ap-breakdown-total">
                              <span className="ap-label">Net impact</span>
                              <span className="ap-breakdown-ref" />
                              <span className="ap-breakdown-amt ap-breakdown-total-amt">
                                {formatCurrency(
                                  (lt.paid_leave && absentWagesEnabled ? wagesAmt : 0) -
                                  (penalty > 0 && absentPenaltyEnabled ? penaltyAmt : 0)
                                )}
                              </span>
                            </div>
                          ) : (
                            <div className="dd-lcp-lop dd-leave-calc-preview">
                              <span className="material-symbols-rounded">info</span>
                              <span>Loss of Pay only — no earnings or deductions</span>
                            </div>
                          )}
                        </div>
                      ) : (
                        <p className="dd-readonly-text" style={{ marginBottom: 14 }}>
                          No deduction rule found for absent status. Create a leave type with code <strong>absent</strong> in Directus to enable automatic deductions.
                        </p>
                      )}
                      <button className="ap-approve-btn" disabled={saving} onClick={handleApproveAbsent}>
                        <span className="material-symbols-rounded">check_circle</span>
                        Approve absence
                      </button>
                    </>
                  );
                })()
              )}
            </section>
          )}

          {/* ── Supervisor read-only notice (any pending record) ── */}
          {isPendingApproval && !isManager && (
            <section className="dd-section">
              <div className="dd-readonly">
                <div className="dd-section-head">
                  <span className="material-symbols-rounded">lock</span>
                  <h4>Pending manager approval</h4>
                </div>
                <p className="dd-readonly-text">
                  This record is awaiting manager approval. Only managers can approve and finalise.
                </p>
              </div>
            </section>
          )}

          {/* ── Earnings (approved + ad-hoc) ── */}
          {/* Visible whenever there are earnings to display OR a manager can add one */}
          {(earnings.length > 0 || isManager) && (
            <section className="dd-section">
              <div className="dd-section-head">
                <span
                  className="material-symbols-rounded"
                  style={{ color: earnings.length > 0 ? '#4d6325' : undefined }}
                >
                  payments
                </span>
                <h4>Earnings</h4>
              </div>

              {/* Existing earnings list */}
              {earnings.length > 0 && (
                <div className="dd-line-list">
                  {earnings.map((e, i) => {
                    const isShiftType = e.type === 'wages' || e.type === 'overtime';
                    const isOT  = e.type === 'overtime';
                    const hours = isOT ? (parseFloat(e.OT_hours) || 0) : (parseFloat(e.wage_hours) || 0);
                    const rate  = isOT ? (parseFloat(e.OT_rate)  || 0) : (parseFloat(e.wage_rate)  || 0);
                    const fmtR  = r => parseFloat(Number(r).toFixed(2));
                    const TYPE_LABELS = { wages: 'Wages', overtime: 'Overtime', bonus: 'Bonus', allowance: 'Allowance' };
                    const TYPE_ICONS  = { wages: 'work', overtime: 'more_time', bonus: 'emoji_events', allowance: 'savings' };
                    return (
                      <div key={i} className="dd-line">
                        <div>
                          <p className="dd-line-kind">
                            <span className="material-symbols-rounded dd-line-type-ico">{TYPE_ICONS[e.type] || 'work'}</span>
                            {TYPE_LABELS[e.type] || e.type || 'Wages'}
                          </p>
                          {/* Wages and OT show compact chips (hours · rate/h); everything else shows remarks as a sub-line */}
                          {isShiftType ? (
                            <div className="dd-line-chips">
                              {hours > 0 && (
                                <span className="dd-chip">
                                  <span className="material-symbols-rounded">schedule</span>{hours}h
                                </span>
                              )}
                              {rate > 0 && (
                                <><span className="dd-chip-sep">·</span>
                                <span className="dd-chip">₹{fmtR(rate)}/h</span></>
                              )}
                              {e.remarks && (
                                <><span className="dd-chip-sep">·</span>
                                <span className="dd-chip">{e.remarks}</span></>
                              )}
                            </div>
                          ) : (
                            e.remarks && <p className="dd-line-ref">{e.remarks}</p>
                          )}
                        </div>
                        <span className="dd-line-amt num pos">{formatCurrency(e.amount)}</span>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* ── Add ad-hoc earning (manager only) ── */}
              {isManager && (
                addEarningOpen ? (
                  <div className="ae-form">
                    {/* Type toggle: bonus or allowance */}
                    <div className="ae-type-row">
                      {[
                        ['bonus',     'emoji_events', 'Bonus'],
                        ['allowance', 'savings',      'Allowance'],
                      ].map(([val, icon, lbl]) => (
                        <button
                          key={val}
                          type="button"
                          className={`ae-type-chip${addEarningType === val ? ' active' : ''}`}
                          onClick={() => setAddEarningType(val)}
                        >
                          <span className="material-symbols-rounded">{icon}</span>{lbl}
                        </button>
                      ))}
                    </div>

                    <div className="ap-field">
                      <span className="ap-label">Amount (₹)</span>
                      <input
                        className="ap-input"
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="0.00"
                        value={addEarningAmount}
                        onChange={e => setAddEarningAmount(e.target.value)}
                        onFocus={e => e.target.select()}
                        autoFocus
                      />
                    </div>

                    <div className="ap-field">
                      <span className="ap-label">Remarks</span>
                      <input
                        className="ap-input"
                        placeholder="Optional note"
                        value={addEarningRemarks}
                        onChange={e => setAddEarningRemarks(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter' && addEarningAmount) handleAddEarning(); }}
                      />
                    </div>

                    <div className="ae-form-actions">
                      <button
                        className="btn btn-ghost"
                        onClick={() => { setAddEarningOpen(false); setAddEarningAmount(''); setAddEarningRemarks(''); }}
                      >
                        Cancel
                      </button>
                      <button
                        className="ap-save"
                        disabled={saving || !addEarningAmount || parseFloat(addEarningAmount) <= 0}
                        onClick={handleAddEarning}
                      >
                        Save earning
                      </button>
                    </div>
                  </div>
                ) : (
                  <button className="ae-add-btn" onClick={() => setAddEarningOpen(true)}>
                    <span className="material-symbols-rounded">add</span>
                    Add bonus or allowance
                  </button>
                )
              )}
            </section>
          )}

          {/* ── Deductions ── */}
          {(deductions.length > 0 || isManager) && (
            <section className="dd-section">
              <div className="dd-section-head">
                <span className="material-symbols-rounded" style={{ color: deductions.length > 0 ? '#8a2f1a' : undefined }}>
                  remove_circle
                </span>
                <h4>Deductions</h4>
              </div>

              {deductions.length > 0 && (
                <div className="dd-line-list">
                  {deductions.map((d, i) => {
                    const DEDUCTION_LABELS = { leave: 'Leave penalty', tax: 'Tax', damages: 'Damages', adhoc: 'Ad-hoc' };
                    const DEDUCTION_ICONS  = { leave: 'beach_access', tax: 'receipt', damages: 'warning', adhoc: 'remove' };
                    return (
                      <div key={i} className="dd-line">
                        <div>
                          <p className="dd-line-kind">
                            <span className="material-symbols-rounded dd-line-type-ico">{DEDUCTION_ICONS[d.type] || 'remove'}</span>
                            {DEDUCTION_LABELS[d.type] || d.type}
                          </p>
                          {d.remarks && <p className="dd-line-ref">{d.remarks}</p>}
                        </div>
                        <span className="dd-line-amt num neg">−{formatCurrency(d.amount)}</span>
                      </div>
                    );
                  })}
                </div>
              )}

              {isManager && (
                addDeductionOpen ? (
                  <div className="ae-form">
                    <div className="ae-type-row">
                      {[
                        ['adhoc',   'remove',      'Ad-hoc'],
                        ['tax',     'receipt',     'Tax'],
                        ['damages', 'warning',     'Damages'],
                        ['leave',   'beach_access','Leave'],
                      ].map(([val, icon, lbl]) => (
                        <button
                          key={val}
                          type="button"
                          className={`ae-type-chip${addDeductionType === val ? ' active' : ''}`}
                          onClick={() => setAddDeductionType(val)}
                        >
                          <span className="material-symbols-rounded">{icon}</span>{lbl}
                        </button>
                      ))}
                    </div>
                    <div className="ap-field">
                      <span className="ap-label">Amount (₹)</span>
                      <input
                        className="ap-input"
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="0.00"
                        value={addDeductionAmount}
                        onChange={e => setAddDeductionAmount(e.target.value)}
                        onFocus={e => e.target.select()}
                        autoFocus
                      />
                    </div>
                    <div className="ap-field">
                      <span className="ap-label">Remarks</span>
                      <input
                        className="ap-input"
                        placeholder="Optional note"
                        value={addDeductionRemarks}
                        onChange={e => setAddDeductionRemarks(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter' && addDeductionAmount) handleAddDeduction(); }}
                      />
                    </div>
                    <div className="ae-form-actions">
                      <button
                        className="btn btn-ghost"
                        onClick={() => { setAddDeductionOpen(false); setAddDeductionAmount(''); setAddDeductionRemarks(''); }}
                      >
                        Cancel
                      </button>
                      <button
                        className="ap-save"
                        disabled={saving || !addDeductionAmount || parseFloat(addDeductionAmount) <= 0}
                        onClick={handleAddDeduction}
                      >
                        Save deduction
                      </button>
                    </div>
                  </div>
                ) : (
                  <button className="ae-add-btn" onClick={() => setAddDeductionOpen(true)}>
                    <span className="material-symbols-rounded">add</span>
                    Add deduction
                  </button>
                )
              )}
            </section>
          )}

          {/* ── Payouts ── */}
          {payouts.length > 0 && (
            <section className="dd-section">
              <div className="dd-section-head">
                <span className="material-symbols-rounded" style={{ color: 'var(--color-primary)' }}>payments</span>
                <h4>Payouts today</h4>
              </div>
              <div className="dd-line-list">
                {payouts.map((p, i) => (
                  <div key={i} className="dd-line">
                    <div>
                      <p className="dd-line-kind">
                        {{ upi: 'UPI', bank: 'Bank Transfer', cash: 'Cash' }[p.mode] || p.mode || 'Payment'}
                      </p>
                      {p.remarks && <p className="dd-line-ref">{p.remarks}</p>}
                    </div>
                    <span className="dd-line-amt num payout">−{formatCurrency(p.amount)}</span>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* ── Notes ── */}
          <section className="dd-section">
            <div className="dd-section-head">
              <span className="material-symbols-rounded">edit_note</span>
              <h4>Notes</h4>
            </div>

            {isManager && (
              <div style={{ display: 'flex', gap: 8, marginBottom: notes.length > 0 ? 10 : 0 }}>
                <input
                  className="ap-input"
                  style={{ flex: 1 }}
                  value={noteContent}
                  onChange={e => setNoteContent(e.target.value)}
                  placeholder="Add a note for this day…"
                  onKeyDown={e => { if (e.key === 'Enter' && noteContent.trim()) handleAddNote(); }}
                />
                <button
                  className="ap-save"
                  style={{ padding: '0 18px', flexShrink: 0 }}
                  onClick={handleAddNote}
                  disabled={saving || !noteContent.trim()}
                >
                  Add
                </button>
              </div>
            )}

            {notes.length > 0 ? (
              <div className="dd-line-list">
                {notes.map((n, i) => (
                  <div key={n.id || i} className="dd-note">
                    <div className="dd-note-meta">
                      <strong>{n.user_created?.first_name || 'Supervisor'}</strong>
                      {isManager && editingNoteId !== n.id && (
                        <button
                          className="dd-note-edit-btn"
                          title="Edit note"
                          onClick={() => { setEditingNoteId(n.id); setEditContent(n.content); }}
                        >
                          <span className="material-symbols-rounded">edit</span>
                        </button>
                      )}
                    </div>
                    {editingNoteId === n.id ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 4 }}>
                        <textarea
                          className="ap-input"
                          style={{ resize: 'vertical', minHeight: 60 }}
                          value={editContent}
                          onChange={e => setEditContent(e.target.value)}
                          autoFocus
                        />
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button
                            className="ap-save"
                            style={{ padding: '4px 14px', fontSize: 13 }}
                            onClick={() => handleSaveNote(n.id)}
                            disabled={saving || !editContent.trim()}
                          >Save</button>
                          <button
                            className="btn btn-ghost"
                            style={{ padding: '4px 14px', fontSize: 13 }}
                            onClick={() => setEditingNoteId(null)}
                          >Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <p>{n.content}</p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="dd-empty">No notes recorded for this day.</p>
            )}
          </section>
        </div>

      </div>

      {/* ── Approve confirmation ── */}
      <Modal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Approve attendance?"
        size="modal-sm"
        footer={
          <>
            <button className="btn btn-ghost" onClick={() => setConfirmOpen(false)}>Cancel</button>
            <button className="btn btn-primary" onClick={handleApproveAttendance} disabled={saving}>
              {saving ? 'Saving…' : 'Confirm & lock'}
            </button>
          </>
        }
      >
        <div className="ap-confirm-warn">
          <span className="material-symbols-rounded" style={{ fontSize: 28 }}>warning</span>
          This is irreversible. Punch records and breaks will be locked permanently after approval.
        </div>
        <div className="ap-confirm-summary">
          <div><span>Worker</span><strong>{worker.name}</strong></div>
          <div><span>Date</span><strong>{dateStr}{isRecordPowerDay ? ' ⚡' : ''}</strong></div>
          <div><span>Payable</span><strong>{payableHrs}h{payableMins > 0 ? ` ${payableMins}m` : ''}</strong></div>
          {powerDayBonus > 0 && (
            <div><span>Power Day Bonus</span><strong>{formatCurrency(powerDayBonus)}</strong></div>
          )}
          <div><span>Total wages</span><strong>{formatCurrency(grandTotal)}</strong></div>
        </div>
      </Modal>

      <PaymentModal
        open={paymentOpen}
        workerId={worker.id}
        workerName={worker.name}
        onClose={() => setPaymentOpen(false)}
        onSaved={() => {}}
      />
    </>
  );
}
